<template>
  <div
    ref="listRef"
    class="references-list"
    :class="{ readonly: !isEditable }"
    @drag="handleDrag"
    @drop="handleDrop"
    @dragover="handleDragOver"
    @dragend="handleDragEnd"
    @click="handleOutsideClick"
  >
    <div v-if="!referencesList.length" class="references-list__empty-state">
      <TextBody tag="span" :i18n-key="emptyStateKey" :i18n-params="{ filterValue }" :body-type="3" weight="medium" />
    </div>
    <ReferenceListItem
      v-else
      v-for="(reference, index) in referencesList"
      :key="`reference-${index}`"
      :index="index"
      :reference="reference"
      :is-selected="isReferenceSelected(reference)"
      :is-editable="isEditable"
      :is-dragging="draggedIndex === reference.index"
      :drop-indicator="dropIndicator.index === index ? dropIndicator.position : null"
      @select="onSelectReference(reference)"
      @remove="() => removeFootnote(index)"
      @update="(value) => onUpdateFootnote(reference, value)"
      @reset="() => resetFootnote(reference)"
      @select-second-order="onSelectSecondOrderReference"
      @update-second-order="onUpdateSecondOrderReference"
      @reset-second-order="onResetSecondOrderReference"
      @remove-second-order="onRemoveSecondOrderReference"
    />
  </div>
</template>

<script>
import { computed, nextTick, ref, watch } from 'vue';
import { useWesley } from '@anthill-modules/wesley-components';
import { useI18n } from 'vue-i18n';

import ReferenceListItem from '~/components/conversation/authoring/references/ReferenceListItem.vue';
import { footnotesWithAuthorsTitleYearPage } from '~/utils/footnotes';
import { getAllSecondOrderReferences } from '~/utils/citation-second-order';
import TextBody from '~/components/common/TextBody.vue';
import useAuthoring from '~/composables/useAuthoring';
import useModals from '~/composables/useModals';
import useDocumentEditor from '~/composables/useDocumentEditor';
import useSecondOrderReferencesGating from '~/composables/useSecondOrderReferencesGating';

export default {
  components: {
    TextBody,
    ReferenceListItem,
  },
  props: {
    isEditable: {
      type: Boolean,
      default: false,
    },
    filterValue: {
      type: String,
      default: '',
    },
  },
  emits: ['update-references'],
  setup(props, { emit }) {
    const { t } = useI18n();
    const draggedIndex = ref(-1);
    const { sendMessage: sendMessageToDocument } = useWesley();
    const { footnotes, setFootnotes } = useAuthoring();
    const { setReferencesListOpened } = useModals();
    const { updateEditorSelectableElementsData, removeOrphanCitationSpans, excludeSecondOrderSourceForDocuments } =
      useDocumentEditor();
    const { isSecondOrderReferencesOrg } = useSecondOrderReferencesGating();

    // Every second-order source cited anywhere across all footnotes, deduped globally (see
    // getAllSecondOrderReferences) — the same source cited by more than one parent document is
    // only listed once, under whichever parent it's first found under (entry.primaryReference),
    // with the rest of its citing documents rolled into entry.documents (see
    // ReferenceListItem.vue's "+N more documents" note).
    const globalSecondOrderReferences = computed(() =>
      isSecondOrderReferencesOrg.value ? getAllSecondOrderReferences(footnotes.value || []) : [],
    );

    const referencesList = computed(() => {
      const list =
        footnotes.value?.map((ref, index) => {
          const ownId = ref.externalId || ref.id;

          // Nested sources cited within this document — shown alongside the parent document
          // itself, so a source referenced only from inside it can also be picked directly.
          // `originalText` is the AI-extracted text, preserved so the popup can show "AI Original"
          // vs "Current" the same way a main entry does once it has a customFootnote;
          // `customFootnote` (persisted on every parent document that cites this source — see
          // onUpdateSecondOrderReference) holds the override itself, and `text` is simply
          // whichever of the two should currently display.
          const secondOrderReferences = globalSecondOrderReferences.value
            .filter((entry) => (entry.primaryReference.externalId || entry.primaryReference.id) === ownId)
            .map((entry) => {
              const customFootnote = ref.secondOrderCustomText?.[entry.key] || '';
              return {
                ...entry,
                originalText: entry.text,
                customFootnote,
                text: customFootnote || entry.text,
                moreDocumentsCount: entry.documents.length - 1,
              };
            });

          return {
            ...ref,
            footnoteString: footnotesWithAuthorsTitleYearPage(ref, false),
            index,
            secondOrderReferences,
          };
        }) || [];

      const query = props.filterValue?.trim().toLowerCase();
      if (!query) {
        return list;
      }

      return list.filter((ref) => ref.footnoteString?.toLowerCase().includes(query));
    });
    const selectedReference = ref(null);
    const listRef = ref(null);

    const emptyStateKey = computed(() => {
      if (props.filterValue?.trim()) return 'author.noResultsForFilter';
      return props.isEditable ? 'author.noReferencesEditable' : 'author.noReferences';
    });

    const insertCitation = (citationData) => {
      sendMessageToDocument({
        method: 'editorCommand',
        params: {
          command: {
            insertCitation: citationData,
          },
        },
      });
      updateEditorSelectableElementsData();
      setReferencesListOpened({ open: false });
      emit('update-references');
    };

    const onSelectReference = (reference) => {
      if (reference && props.isEditable) {
        selectedReference.value = reference;
      } else {
        insertCitation({
          id: reference.externalId || reference.id,
          page: 1,
          index: 1,
        });
      }
    };

    // Second-order rows are derived data (see getAllSecondOrderReferences), not their own footnote
    // entry — there's nothing to select/manage for them, so picking one always inserts a citation
    // for its primary (first-found) parent document, anchored to the snippet that source was
    // actually found under, regardless of whether the popup is in manage (isEditable) or insert mode.
    const onSelectSecondOrderReference = (entry) => {
      insertCitation({
        id: entry.primaryReference.externalId || entry.primaryReference.id,
        page: entry.page || 1,
        index: entry.snippetIndex ?? 1,
      });
    };

    const isReferenceSelected = (reference) =>
      selectedReference.value &&
      selectedReference.value.externalId === reference.externalId &&
      selectedReference.value.index === reference.index;

    // referencesList's items carry secondOrderReferences as a display-only computed field —
    // rebuilt fresh every render from second_order_references_by_snippet + secondOrderCustomText
    // (see referencesList above), and bulky (each entry embeds full primaryReference/documents
    // footnote objects). It must never itself be persisted; secondOrderCustomText (the actual
    // override data) is the only thing that needs to survive a save.
    const stripDerivedFields = ({ secondOrderReferences, ...footnote }) => footnote;

    const onUpdateFootnote = (footnote, text) => {
      if (footnote && text && props.isEditable) {
        const updatedList = referencesList.value.map((item) => {
          if (item.index === footnote.index) {
            if (item.addedByUser) {
              return { ...item, name: text, customFootnote: text };
            }
            return { ...item, customFootnote: text };
          }
          return item;
        });
        setFootnotes(updatedList.map(stripDerivedFields));
      }
    };

    const resetFootnote = (footnote) => {
      if (!footnote || footnote.addedByUser || !props.isEditable) return;

      const updatedList = referencesList.value.map((item) => {
        if (item.index === footnote.index) {
          const { customFootnote, ...rest } = item;
          return rest;
        }
        return item;
      });
      setFootnotes(updatedList.map(stripDerivedFields));
    };

    const dropIndicator = ref({ index: -1, position: null });

    // Mirrors onUpdateFootnote above, but for one second-order source rather than a footnote
    // itself. The same source can be cited by more than one parent document (entry.documents —
    // see getAllSecondOrderReferences), and the printed reference list/Preview numbering already
    // treats it as a single shared citation across all of them — so the override is written onto
    // every one of those parent documents, not just whichever one the popup happened to show it
    // under, or it would silently stop applying to the others.
    const onUpdateSecondOrderReference = ({ entry, text } = {}) => {
      if (!text || !entry?.key || !props.isEditable) return;

      const documentIds = new Set((entry.documents || []).map((doc) => doc.externalId || doc.id));

      const updatedList = referencesList.value.map((item) => {
        const itemId = item.externalId || item.id;
        if (!documentIds.has(itemId)) return item;

        return {
          ...item,
          secondOrderCustomText: { ...(item.secondOrderCustomText || {}), [entry.key]: text },
        };
      });
      setFootnotes(updatedList.map(stripDerivedFields));
    };

    // Mirrors resetFootnote above, but clears one second-order source's override rather than a
    // whole footnote's — same "write to every citing document" reasoning as
    // onUpdateSecondOrderReference, so the reset applies everywhere the source is cited, not just
    // wherever the popup happened to show it from.
    const onResetSecondOrderReference = (entry) => {
      if (!entry?.key || !props.isEditable) return;

      const documentIds = new Set((entry.documents || []).map((doc) => doc.externalId || doc.id));

      const updatedList = referencesList.value.map((item) => {
        const itemId = item.externalId || item.id;
        if (!documentIds.has(itemId) || !item.secondOrderCustomText) return item;

        const { [entry.key]: _removed, ...remainingCustomText } = item.secondOrderCustomText;
        return { ...item, secondOrderCustomText: remainingCustomText };
      });
      setFootnotes(updatedList.map(stripDerivedFields));
    };

    // Deletes one second-order source from every document that cites it (same "write to every
    // citing document" scope as onUpdateSecondOrderReference/onResetSecondOrderReference above) —
    // each real citation span sharing this parent id gets the source added to its own
    // data-excluded-second-order-refs, which is exactly what the in-text per-row delete already
    // writes (see citation-dom.js), so reconciliation drops the now-excluded source's span on its
    // own next safe render pass, no direct DOM removal needed here.
    const onRemoveSecondOrderReference = (entry) => {
      if (!entry?.key || !props.isEditable) return;

      const documentIds = (entry.documents || []).map((doc) => doc.externalId || doc.id);
      excludeSecondOrderSourceForDocuments(documentIds, entry.key);
    };

    const handleDrag = (event) => {
      draggedIndex.value = parseInt(event.target.dataset.index, 10);
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', draggedIndex.value); // Pass the index of the dragged item
      event.target.classList.add('dragging-item'); // Add dragging styles
    };

    const handleDragOver = (event) => {
      event.preventDefault();

      const targetElement = event.target.closest('.draggable-wrapper');
      const targetIndex = targetElement ? parseInt(targetElement.dataset.index, 10) : NaN;

      if (isNaN(targetIndex) || targetIndex === draggedIndex.value) {
        dropIndicator.value = { index: -1, position: null };
        return;
      }

      const { top, height } = targetElement.getBoundingClientRect();
      const position = event.clientY - top < height / 2 ? 'before' : 'after';

      dropIndicator.value = { index: targetIndex, position };
    };

    const handleDragEnd = () => {
      draggedIndex.value = -1;
      dropIndicator.value = { index: -1, position: null };
    };

    const handleDrop = (event) => {
      event.preventDefault();
      const { index: targetIndex, position } = dropIndicator.value;

      if (!isNaN(draggedIndex.value) && !isNaN(targetIndex) && targetIndex >= 0 && draggedIndex.value !== targetIndex) {
        const updatedList = [...referencesList.value];
        const draggedItem = updatedList.splice(draggedIndex.value, 1)[0];

        let insertAt = position === 'after' ? targetIndex + 1 : targetIndex;
        if (draggedIndex.value < insertAt) {
          insertAt -= 1;
        }

        updatedList.splice(insertAt, 0, draggedItem);
        updateIndexAndFoootnotesList(updatedList);
      }

      draggedIndex.value = -1;
      dropIndicator.value = { index: -1, position: null };
    };

    const updateIndexAndFoootnotesList = (updatedList) => {
      updatedList.forEach((item, newIndex) => {
        item.index = newIndex;
      });

      const updatedFootnotes = updatedList.sort((a, b) => a.index - b.index).map(stripDerivedFields); // Sort by index

      setFootnotes(updatedFootnotes);
      selectedReference.value = null;
    };

    const removeFootnote = async (indexToRemove) => {
      const originalList = [...referencesList.value];
      const updatedList = originalList.filter((_, index) => index !== indexToRemove);

      updateIndexAndFoootnotesList(updatedList);
      try {
        await removeOrphanCitationSpans(updatedList);
      } catch (e) {
        console.log('Error removing orphan citation spans', e);
      }
    };

    const handleOutsideClick = (event) => {
      const reference = event.target.closest('.references-wrapper');

      if (!reference) {
        selectedReference.value = null;
      }
    };


    watch(
      () => footnotes.value?.length,
      (newLength, oldLength) => {
        if (newLength > oldLength) {
          nextTick(() => {
            if (listRef.value) {
              listRef.value.scrollTop = listRef.value.scrollHeight;
            }
          });
        }
      },
    );

    return {
      referencesList,
      isReferenceSelected,
      handleDrag,
      handleDragOver,
      handleDragEnd,
      handleDrop,
      dropIndicator,
      selectedReference,
      emptyStateKey,
      listRef,
      onSelectReference,
      onSelectSecondOrderReference,
      onUpdateSecondOrderReference,
      onResetSecondOrderReference,
      onRemoveSecondOrderReference,
      removeFootnote,
      onUpdateFootnote,
      resetFootnote,
      draggedIndex,
      handleOutsideClick,
      t,
    };
  },
};
</script>

<style scoped lang="scss">
.references-list {
  display: flex;
  flex-direction: column;
  gap: $bb-spacing-4;
  padding: 16px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  background-color: $bb-neutral-6;

  &.readonly {
    cursor: not-allowed;
    opacity: 0.6;
  }

  .dragging-item {
    opacity: 0.5;
    background-color: #f0f0f0;
  }

  &__empty-state {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;

    :deep(span) {
      font-size: 16px;
    }
  }
}
</style>
